import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/EnterpriseAuthContext';
import { retryWithBackoff } from '@/lib/retry-utils';
import { mapError } from '@/lib/api-errors';
import { PROFILE_QUERY_KEYS, PROFILE_ERRORS, type UserProfile } from './shared';
import * as Sentry from '@sentry/react';

/**
 * Options for configuring the profile query behavior
 */
export interface UseProfileQueryOptions {
  /** Enable/disable the query */
  enabled?: boolean;
  /** Time in ms before data is considered stale */
  staleTime?: number;
  /** Time in ms before inactive data is garbage collected */
  gcTime?: number;
  /** Refetch when window regains focus */
  refetchOnWindowFocus?: boolean;
  /** Refetch when component mounts */
  refetchOnMount?: boolean;
}

/**
 * Fetches the current user's profile from Supabase
 * Includes RLS verification and error handling
 */
async function fetchProfile(userId: string): Promise<UserProfile | null> {
  try {
    const { data, error } = await retryWithBackoff(
      async () => {
        const result = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle();
        return result;
      },
      { maxRetries: 3, baseDelay: 1000 }
    );

    if (error) {
      throw mapError(error, 'database');
    }

    // Verify RLS - data should belong to current user
    if (data && data.user_id !== userId) {
      const rlsError = new Error('RLS Policy Violation: Retrieved data from different user');
      Sentry.captureException(rlsError, {
        tags: { context: 'profile_security', operation: 'rls_violation' },
        extra: { requestedUserId: userId, returnedUserId: data.user_id },
      });
      throw mapError(rlsError, 'database');
    }

    return data as unknown as UserProfile | null;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { context: 'profile_error', operation: 'fetch_profile' },
      extra: { userId },
    });
    throw error;
  }
}

/**
 * Hook for fetching user profile data with React Query
 * 
 * Features:
 * - Automatic retry with exponential backoff
 * - RLS verification
 * - Stale-while-revalidate caching
 * - Optimistic updates support
 * 
 * @param options - Configuration options for the query
 * @returns Query result with profile data and helper functions
 */
export function useProfileQuery(options: UseProfileQueryOptions = {}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: PROFILE_QUERY_KEYS.detail(user?.id || ''),
    queryFn: async () => {
      if (!user?.id) {
        throw new Error(PROFILE_ERRORS.NOT_AUTHENTICATED);
      }
      return fetchProfile(user.id);
    },
    enabled: options.enabled !== false && !!user?.id,
    staleTime: options.staleTime ?? 5 * 60 * 1000, // 5 minutes default
    gcTime: options.gcTime ?? 10 * 60 * 1000, // 10 minutes default
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? true,
    refetchOnMount: options.refetchOnMount ?? true,
    retry: (failureCount, error) => {
      // Don't retry on authentication errors
      if (error instanceof Error && error.message.includes('not authenticated')) {
        return false;
      }
      // Retry up to 3 times with exponential backoff
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => {
      // Exponential backoff: 1s, 2s, 4s
      return Math.min(1000 * 2 ** attemptIndex, 10000);
    },
  });

  /**
   * Force refetch profile data and invalidate all related queries
   */
  const refetch = useCallback(async () => {
    if (!user?.id) return;
    
    // Invalidate all profile-related queries
    await queryClient.invalidateQueries({
      queryKey: PROFILE_QUERY_KEYS.all,
    });
    
    return query.refetch();
  }, [user?.id, queryClient, query]);

  /**
   * Remove profile data from cache
   * Useful when user logs out or switches accounts
   */
  const invalidateCache = useCallback(() => {
    if (!user?.id) return;
    
    queryClient.removeQueries({
      queryKey: PROFILE_QUERY_KEYS.detail(user.id),
    });
  }, [user?.id, queryClient]);

  return {
    ...query,
    refetch,
    invalidateCache,
  };
}
